// /src/services/smsService.js
const PRIMARY_URL = process.env.REACT_APP_SMS_API_URL || "https://sendsms-kiyzzuorfq-du.a.run.app";
// (옵션) 레거시 Functions 용(아이돌봄에서 쓰던 smsSend)
// ex) https://asia-northeast3-<PROJECT>.cloudfunctions.net/smsSend
const LEGACY_URL = process.env.REACT_APP_SMS_API_URL_LEGACY || "";

// 6자리 000000~999999
export function genOtp(len = 6) {
    const n = Math.floor(Math.random() * Math.pow(10, len));
    return String(n).padStart(len, "0");
}

// 010-1234-5678 → +821012345678
export function toE164KR(digits) {
    if (!digits) return "";
    let d = String(digits).replace(/\D/g, "");
    if (d.startsWith("0")) d = d.slice(1);
    return `+82${d}`;
}

// QA/테스트 번호 범위
export function inTestRange(digits) {
    const d = String(digits || "").replace(/\D/g, "");
    return d.startsWith("0100000"); // 예: 010-0000-****
}

// 내부: fetch with timeout
async function postJson(url, body, { timeoutMs = 8000 } = {}) {
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort("timeout"), timeoutMs);
    try {
        const resp = await fetch(url, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            // CORS 프리플라이트 대비: 추가 헤더 최소화
            body: JSON.stringify(body),
            signal: ctrl.signal,
            // mode 기본값 'cors'
            // credentials 기본값 'same-origin'
        });
        let data = null, text = "";
        try { data = await resp.json(); } catch { try { text = await resp.text(); } catch { } }
        return { resp, data, text };
    } finally {
        clearTimeout(t);
    }
}

// 서버별 페이로드 어댑터
function buildPayloadFor(url, digits, otp) {
    const to = String(digits).replace(/\D/g, "");
    // 1) 기존 Cloud Run 템플릿 방식
    if (/sendsms|run\.app/i.test(url)) {
        return { to, templateId: "VERIFY_CODE", variables: { code: otp } };
    }
    // 2) 레거시 Cloud Functions(smsSend) 방식 (일반적으로 이런 형태를 많이 씀)
    if (/smsSend|cloudfunctions\.net/i.test(url)) {
        return { to, template: "VERIFY_CODE", code: otp };
    }
    // 3) 모를 때는 템플릿 방식으로
    return { to, templateId: "VERIFY_CODE", variables: { code: otp } };
}

/**
 * OTP 전송
 * - PRIMARY_URL 실패 시 LEGACY_URL로 1회 폴백
 * - 항상 { ok, status, error, data, tried } 형태 반환
 */
export async function sendOtp(digits, otp) {
    const tried = [];
    // 0) 혼합콘텐츠 방지: http 페이지에서 https 호출만 허용됨
    if (window?.location?.protocol === "http:" && /^https:/.test(PRIMARY_URL) === false) {
        return { ok: false, status: 0, error: "Mixed content: HTTPS endpoint required.", tried };
    }

    // 1) 1차 — PRIMARY_URL
    try {
        const payload = buildPayloadFor(PRIMARY_URL, digits, otp);
        const { resp, data, text } = await postJson(PRIMARY_URL, payload);
        tried.push({ url: PRIMARY_URL, status: resp.status, body: data || text });

        const ok = resp.ok && (data ? data.ok !== false : true);
        if (ok) return { ok: true, status: resp.status, data, tried };

        const errMsg = (data && (data.error || data.result?.message)) || (text && text.slice(0, 300)) || `HTTP ${resp.status}`;
        // FALLTHROUGH → 레거시 폴백 시도
    } catch (e) {
        tried.push({ url: PRIMARY_URL, error: String(e?.message || e) });
        // FALLTHROUGH → 레거시 폴백 시도
    }

    // 2) 2차 — LEGACY_URL (있을 때만)
    if (LEGACY_URL) {
        try {
            const payload2 = buildPayloadFor(LEGACY_URL, digits, otp);
            const { resp: r2, data: d2, text: t2 } = await postJson(LEGACY_URL, payload2);
            tried.push({ url: LEGACY_URL, status: r2.status, body: d2 || t2 });

            const ok2 = r2.ok && (d2 ? d2.ok !== false : true);
            if (ok2) return { ok: true, status: r2.status, data: d2, tried };

            const err2 = (d2 && (d2.error || d2.result?.message)) || (t2 && t2.slice(0, 300)) || `HTTP ${r2.status}`;
            return { ok: false, status: r2.status, error: err2, data: d2, tried };
        } catch (e2) {
            tried.push({ url: LEGACY_URL, error: String(e2?.message || e2) });
            return { ok: false, status: 0, error: String(e2?.message || e2), tried };
        }
    }

    // 3) 폴백도 실패
    return { ok: false, status: 0, error: "Network/CORS failure", tried };
}
